import React, { type ReactNode, type Ref, type RefObject, useCallback, useMemo } from 'react';

import { loadEntryPoint } from 'react-relay';
import { useMergeRefs } from 'use-callback-ref';

import type {
	AnyEntryPoint,
	EntryPointRef,
	ParamsOfEntryPoint,
	RuntimePropsOfEntryPoint,
} from '@atlassian/entry-point-types';
import { usePressablePreloadRef } from '@atlassian/entry-point-use-trigger';
import {
	type ErrorFallback,
	InternalEntryPointContainer,
} from '@atlassian/internal-entry-point-container';
import type { JSResourceReference } from '@atlassian/react-async';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { DefaultFallback } from './DefaultFallback';
import type { TriggerContext } from './types';

const emptyEntryPointParams = {} as ParamsOfEntryPoint<AnyEntryPoint>;
const emptyEntryPointProps = {} as RuntimePropsOfEntryPoint<AnyEntryPoint>;

export type Props<TEntryPoint> = {
	children: ({ ref }: { ref: RefObject<HTMLElement> }) => ReactNode;
	entryPoint: TEntryPoint;
	entryPointParams?: ParamsOfEntryPoint<TEntryPoint>;
	entryPointProps?: RuntimePropsOfEntryPoint<TEntryPoint>;
	errorFallback?: ErrorFallback;
	fallback?: ReactNode;
	forwardedRef?: Ref<HTMLElement>;
	loadOnFocus?: boolean;
	trigger: TriggerContext;
};

export function Trigger<TEntryPoint extends AnyEntryPoint>({
	children,
	entryPoint,
	entryPointParams,
	entryPointProps,
	fallback,
	errorFallback,
	forwardedRef,
	trigger,
	loadOnFocus = false,
}: Props<TEntryPoint>) {
	const { setPanel } = trigger;
	const environmentProvider = useRelayEnvironmentProvider();

	const id = useMemo((): string => {
		return (entryPoint.root as JSResourceReference<any>).getModuleName() || 'unknown';
	}, [entryPoint]);

	const load = useCallback(
		() =>
			loadEntryPoint<TEntryPoint>(
				environmentProvider,
				entryPoint,
				entryPointParams ?? emptyEntryPointParams,
			),
		[entryPoint, entryPointParams, environmentProvider],
	);

	const onLoad = useCallback(
		({ reference: entryPointReference }: { reference: EntryPointRef<TEntryPoint> }) => {
			setPanel(
				<InternalEntryPointContainer
					id={id}
					entryPointReference={entryPointReference}
					fallback={fallback || <DefaultFallback />}
					errorFallback={errorFallback}
					runtimeProps={entryPointProps || emptyEntryPointProps}
				/>,
			);
		},
		[setPanel, id, entryPointProps, fallback, errorFallback],
	);

	const triggerRef = usePressablePreloadRef<EntryPointRef<TEntryPoint>>({
		load,
		onLoad,
		loadOnFocus,
	});

	const mergedRef = useMergeRefs([triggerRef, forwardedRef || null]);

	return children({ ref: mergedRef as RefObject<HTMLElement> });
}
